import { Vector3 } from "three";
import { GeometryUtils } from "./internal";
const sin = Math.sin;
const cos = Math.cos;
function precSum(...params) {
    let res = 0.0;
    for (const e of params) {
        res += Math.pow(e, 2);
    }
    return Math.sqrt(res);
}
function precPointXGlobal(args) {
    const f = args;
    const r = [
        // x
        cos(f.phi) * cos(f.kappa) * f.precX,
        // y
        -cos(f.phi) * sin(f.kappa) * f.precY,
        // z
        sin(f.phi) * f.precZ,
        // omega
        0.0 * f.precOmega,
        // phi
        (-(sin(f.phi) * cos(f.kappa) * f.x) + sin(f.phi) * sin(f.kappa) * f.y + cos(f.phi) * f.z) * f.precPhi,
        // kappa
        (-(cos(f.phi) * sin(f.kappa) * f.x) - cos(f.phi) * cos(f.kappa) * f.y) * f.precKappa,
        // tx
        1.0 * f.precTx,
        // ty
        0.0 * f.precTy,
        // tz
        0.0 * f.precTz,
    ];
    return precSum(...r);
}
function precPointYGlobal(args) {
    const f = args;
    const r = [
        // x
        (cos(f.omega) * sin(f.kappa) + sin(f.omega) * sin(f.phi) * cos(f.kappa)) * f.precX,
        // y
        (cos(f.omega) * cos(f.kappa) - sin(f.omega) * sin(f.phi) * sin(f.kappa)) * f.precY,
        // z
        -sin(f.omega) * cos(f.phi) * f.precZ,
        // omega
        (-sin(f.omega) * sin(f.kappa) * f.x +
            cos(f.omega) * sin(f.phi) * cos(f.kappa) * f.x -
            sin(f.omega) * cos(f.kappa) * f.y -
            cos(f.omega) * sin(f.phi) * sin(f.kappa) * f.y -
            cos(f.omega) * cos(f.phi) * f.z) *
            f.precOmega,
        // phi
        (sin(f.omega) * cos(f.phi) * cos(f.kappa) * f.x -
            sin(f.omega) * cos(f.phi) * sin(f.kappa) * f.y +
            sin(f.omega) * sin(f.phi) * f.z) *
            f.precPhi,
        // kappa
        (cos(f.omega) * cos(f.kappa) * f.x -
            sin(f.omega) * sin(f.phi) * sin(f.kappa) * f.x -
            cos(f.omega) * sin(f.kappa) * f.y -
            sin(f.omega) * sin(f.phi) * cos(f.kappa) * f.y) *
            f.precKappa,
        // tx
        0.0 * f.precTx,
        // ty
        1.0 * f.precTy,
        // tz
        0.0 * f.precTz,
    ];
    return precSum(...r);
}
function precPointZGlobal(args) {
    const f = args;
    const r = [
        // x
        (sin(f.omega) * sin(f.kappa) - cos(f.omega) * sin(f.phi) * cos(f.kappa)) * f.precX,
        // y
        (sin(f.omega) * cos(f.kappa) + cos(f.omega) * sin(f.phi) * sin(f.kappa)) * f.precY,
        // z
        cos(f.omega) * cos(f.phi) * f.precZ,
        // omega
        (cos(f.omega) * sin(f.kappa) * f.x +
            sin(f.omega) * sin(f.phi) * cos(f.kappa) * f.x +
            cos(f.omega) * cos(f.kappa) * f.y -
            sin(f.omega) * sin(f.phi) * sin(f.kappa) * f.y -
            sin(f.omega) * cos(f.phi) * f.z) *
            f.precOmega,
        // phi
        (-(cos(f.omega) * cos(f.phi) * cos(f.kappa) * f.x) +
            cos(f.omega) * cos(f.phi) * sin(f.kappa) * f.y -
            cos(f.omega) * sin(f.phi) * f.z) *
            f.precPhi,
        // kappa
        (sin(f.omega) * cos(f.kappa) * f.x +
            cos(f.omega) * sin(f.phi) * sin(f.kappa) * f.x -
            sin(f.omega) * sin(f.kappa) * f.y +
            cos(f.omega) * sin(f.phi) * cos(f.kappa) * f.y) *
            f.precKappa,
        // tx
        0.0 * f.precTx,
        // ty
        0.0 * f.precTy,
        // tz
        1.0 * f.precTz,
    ];
    return precSum(...r);
}
function precPointXLocal(args) {
    const f = args;
    const r = [
        // x
        (f.b / f.p) * f.precX,
        // y
        0.0 * f.precY,
        // p
        -(f.x * f.b * (1.0 / Math.pow(f.p, 2.0))) * f.precP,
        // c
        0.0 * f.precC,
        // b
        (f.x / f.p) * f.precB,
    ];
    return precSum(...r);
}
function precPointYLocal(args) {
    const f = args;
    const r = [
        // x
        0.0 * f.precX,
        // y
        (f.b / f.p) * f.precY,
        // p
        -(f.y * f.b * (1.0 / Math.pow(f.p, 2.0))) * f.precP,
        // c
        0.0 * f.precC,
        // b
        (f.y / f.p) * f.precB,
    ];
    return precSum(...r);
}
function precPointZLocal(args) {
    const f = args;
    const r = [
        // x
        0.0 * f.precX,
        // y
        0.0 * f.precY,
        // p
        f.c * f.b * (1.0 / Math.pow(f.p, 2.0)) * f.precP,
        // c
        (f.b / f.p) * f.precC,
        // b
        -(f.c / f.p) * f.precB,
    ];
    return precSum(...r);
}
function precLine(args) {
    const precPoints = [];
    for (const point of args) {
        precPoints.push(precSum(point.x, point.y, point.z));
    }
    return precSum(...precPoints);
}
function precLine2D(args) {
    const precPoints = [];
    for (const point of args) {
        precPoints.push(precSum(point.x, point.y));
    }
    return precSum(...precPoints);
}
function precArea2D(points, precisions) {
    let res = 0.0;
    for (let i = 0; i < points.length; i++) {
        let iM = (i - 1) % points.length;
        if (i - 1 < 0)
            iM = (i - 1 + points.length) % points.length;
        const iP = (i + 1) % points.length;
        res +=
            (((points[iP].y - points[iM].y) / 2.0) * precisions[i].x) ** 2 +
                (((points[iM].x - points[iP].x) / 2.0) * precisions[i].y) ** 2;
    }
    return Math.sqrt(res);
}
function precArea(points, precisions) {
    const plane = GeometryUtils.FitPlane(points);
    if (plane != null) {
        for (const e of points) {
            e.applyMatrix4(plane.transform);
        }
        return precArea2D(points, precisions);
    }
    return 0.0;
}
export class PrecisionUtils {
    static sum(...args) {
        return precSum(...args);
    }
    static pointGlobal(args) {
        return {
            x: precPointXGlobal(args),
            y: precPointYGlobal(args),
            z: precPointZGlobal(args),
        };
    }
    static pointLocal(args) {
        return {
            x: precPointXLocal(args),
            y: precPointYLocal(args),
            z: precPointZLocal(args),
        };
    }
    static line(args) {
        return precLine(args);
    }
    static line2D(args) {
        return precLine2D(args);
    }
    static lineLocal(args) {
        return precLine(args.map((e) => {
            return { x: e.precLocalEasting, y: e.precLocalNorthing, z: e.precLocalHeight };
        }));
    }
    static lineGlobal(args) {
        return precLine(args.map((e) => {
            return { x: e.precGlobalEasting, y: e.precGlobalNorthing, z: e.precGlobalHeight };
        }));
    }
    static lineLocal2D(args) {
        return precLine2D(args.map((e) => {
            return { x: e.precLocalEasting, y: e.precLocalNorthing };
        }));
    }
    static lineGlobal2D(args) {
        return precLine2D(args.map((e) => {
            return { x: e.precGlobalEasting, y: e.precGlobalNorthing };
        }));
    }
    static heightLineLocal(args) {
        if (args.length < 2)
            return 0.0;
        const first = args[0];
        const last = args[args.length - 1];
        return precSum(first.precLocalHeight, last.precLocalHeight);
    }
    static heightLineGlobal(args) {
        if (args.length < 2)
            return 0.0;
        const last = args[args.length - 1];
        // FIXME: naming of Height
        return precSum(last.precGlobalHeight, last.precGlobalHeight);
    }
    // NOTE: area and area2 with pure js types interface is missing,
    // not like linep, but could be added.
    static areaLocal(args) {
        const points = args.map((e) => {
            return new Vector3(e.Easting, e.Northing, e.Height);
        });
        const precision = args.map((e) => {
            return new Vector3(e.precLocalEasting, e.precLocalNorthing, e.precLocalHeight);
        });
        return precArea(points, precision);
    }
    static areaGlobal(args) {
        const points = args.map((e) => {
            return new Vector3(e.Easting, e.Northing, e.Height);
        });
        const precision = args.map((e) => {
            return new Vector3(e.precGlobalEasting, e.precGlobalNorthing, e.precGlobalHeight);
        });
        return precArea(points, precision);
    }
    static area2DLocal(args) {
        const points = args.map((e) => {
            return new Vector3(e.Easting, e.Northing, e.Height);
        });
        const precision = args.map((e) => {
            return new Vector3(e.precLocalEasting, e.precLocalNorthing, e.precLocalHeight);
        });
        return precArea2D(points, precision);
    }
    static area2DGlobal(args) {
        const points = args.map((e) => {
            return new Vector3(e.Easting, e.Northing, e.Height);
        });
        const precision = args.map((e) => {
            return new Vector3(e.precGlobalEasting, e.precGlobalNorthing, e.precGlobalHeight);
        });
        return precArea2D(points, precision);
    }
}
//# sourceMappingURL=precisionutils.js.map